import React, { useEffect } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router'
import CustomModal from './app/custom/CustomModal'
import LandlordLayout from './components/landlordModule/LandlordLayout'
import LandlordSettings from './components/landlordModule/settings/LandlordSettings'
import { useAppDispatch, useAppSelector } from './hooks/redux'
import AuthPage from './pages/AuthPage'
import LandlordProfile from './components/landlordModule/profile/me/LandlordProfile'
import MyServicesSubscriptions from './components/landlordModule/servicesSubscriptions'
import ChatListSh from './components/shared/newChat/ChatListSh'
import Search from './components/shared/search'
import { type RootState } from './store/store'

import SetUnitModal from './app/custom/customStoreModal/SetUnitModal'
import RelistUnitModal from './app/custom/customStoreModal/RelistUnitModal'
import VoicePlayerLayout from './components/shared/voice-player/VoicePlayerLayout'
import MyReferrals from './components/shared/referrals/myReferrals'
import TutorialsList from './components/agentModule/settings/tutorials/TutorialsList'
import TermsSection from './components/agentModule/menu/termsPrivacy/TermsSection'
import PrivacySection from './components/agentModule/menu/termsPrivacy/PrivacySection'
import { useLocation } from 'react-router-dom'
import OtherAgentsProfile from './components/agentModule/profile/other/viewOtherProfile/OtherAgentsProfile'
import { ACCESS_KEY, USER_ID_KEY } from './urls'

import LandlordMenuPage from './components/landlordModule/menu'
import ViewingActions from './components/landlordModule/adminActions/viewings'
import OffersActions from './components/landlordModule/adminActions/offers'
import { fetchAppConfig, getUserDevice } from './store/actionsCreators/appActions'
import AdvertiseActions from './components/landlordModule/adminActions/advertises'
import ScheduleSh from './components/shared/schedule/ScheduleSh'
import LoadingDeleteAccount from './app/custom/loaders/LoadingDeleteAccount'
import Loader from './app/custom/loaders/Loader'
import TasksActions from './components/landlordModule/adminActions/tasks'
import PortalRequestActions from './components/landlordModule/adminActions/portalRequest'
import MyUnitsWithFiltersSh from './components/shared/newUnits/MyUnitsWithFiltersSh'


import MyCustomModalForView from './app/custom/customModalForView/MyCustomModalForView'
import NewChatV2Sh from './components/shared/newChat/NewChatV2Sh'
import Layout from './app/layout/Layout'
import CustomModalV2 from "./app/custom/modalV2/CustomModalV2";
import ServicesV2 from "./components/landlordModule/services/ServicesV2/ServicesV2";
import { useDynamicDeviceScaling } from './hooks/useDynamicDeviceScaling'
import AppShell from './components/layout/AppShell'
import useMediaQuery from './hooks/useMediaQuery'
import AdminUnitStatPage from './components/adminModule/unitStatPage/AdminUnitStatPage'
import LandlordEventsPageSafe from './components/adminModule/LandlordEventsPageSafe'
import AdminModule from './components/landlordModule/AdminModule'
import LandlordServiceOrdersPage from './components/adminModule/clientOrders/LandlordServiceOrdersPage'
import AdminBsoUnitStatisticsPage from './components/adminModule/bsoUnitStatistics/AdminBsoUnitStatisticsPage'
import AdminBsoUnitActivityPage from './components/adminModule/bsoUnitStatistics/AdminBsoUnitActivityPage'
import SponsoredPartnersAdminPage from './components/adminModule/sponsoredPartners/SponsoredPartnersAdminPage'
import InsiderAdminPage from './components/adminModule/insider/InsiderAdminPage'
import { fetchUser } from './store/actionsCreators/userActions'
import { authSlice } from './store/slices/authSlice'
import LegacyAppMovedNotice from './components/shared/LegacyAppMovedNotice/LegacyAppMovedNotice'
import { LEGACY_APP_HOME_ROUTE, LEGACY_APP_MOVED_ROUTE } from './config/legacyAccess'
import { adminRoleHomeRoute, getAdminRole, isAdminRouteAllowed, type AdminRole } from './config/adminRoles'
import KeyViewingsArchivePage from './components/adminModule/keyViewings/KeyViewingsArchivePage'
import BrokerAnalyticsPage from './components/adminModule/brokerAnalytics/BrokerAnalyticsPage'

const bootstrapErrorMessage = (value: unknown): string => {
    if (typeof value === 'string' && value.trim().length > 0) return value
    if (value != null && typeof value === 'object' && typeof (value as { message?: unknown }).message === 'string') {
        return (value as { message: string }).message
    }
    return 'Could not load your access profile.'
}

function LegacyProtectedRoute({ children, accessReady, accessAllowed }: { children: JSX.Element, accessReady: boolean, accessAllowed: boolean }): JSX.Element {
    const isAuthenticated = useAppSelector((state: RootState) => state.auth.isAuthenticated)

    if (!isAuthenticated) return <Navigate to="/auth/login" replace />
    if (!accessReady) return <Loader />
    if (!accessAllowed) return <Navigate to={LEGACY_APP_MOVED_ROUTE} replace />

    return children
}

function LegacyMovedRoute({ accessReady, accessAllowed }: { accessReady: boolean, accessAllowed: boolean }): JSX.Element {
    const isAuthenticated = useAppSelector((state: RootState) => state.auth.isAuthenticated)

    if (!isAuthenticated) return <Navigate to="/auth/login" replace />
    if (!accessReady) return <Loader />
    if (accessAllowed) return <Navigate to={LEGACY_APP_HOME_ROUTE} replace />

    return <LegacyAppMovedNotice />
}

function AdminRoleRouteGuard({ role, children }: { role: AdminRole, children: JSX.Element }): JSX.Element {
    const { pathname } = useLocation()
    const home = adminRoleHomeRoute(role)
    if (pathname === '/landlord' || !isAdminRouteAllowed(role, pathname)) return <Navigate to={home} replace />
    return children
}

export default function App(): JSX.Element {
    const isAuthenticated: boolean = useAppSelector((state: RootState) => state.auth.isAuthenticated)
    const currentUser = useAppSelector((state: RootState) => state.user.currentUser as { id?: number | string, email?: string })
    const deletingAccount = useAppSelector((state: RootState) => state.user.deletingAccount)
    const dispatch = useAppDispatch()
    const userID = window.localStorage.getItem(USER_ID_KEY)
    const jwt = window.localStorage.getItem(ACCESS_KEY)
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const isDesktop = useMediaQuery('(min-width: 992px)');
    const [legacyUserBootstrapRequested, setLegacyUserBootstrapRequested] = React.useState(false)
    const [bootstrapError, setBootstrapError] = React.useState<string | null>(null)
    const hasAuthenticatedCurrentUser = Number.isFinite(Number(currentUser?.id))
    const adminRole = getAdminRole(currentUser)
    const accessAllowed = adminRole != null
    const accessReady = !isAuthenticated || hasAuthenticatedCurrentUser || bootstrapError != null
    const isMobileCanvas = useMediaQuery('(max-width: 480px)'); // или 540

    useDynamicDeviceScaling({ enabled: isMobileCanvas });

    const appEntryRoute = '/auth/login'

    useEffect(() => {
        const getFirstData = async (): Promise<void> => {

            await dispatch(getUserDevice())
        }
        void getFirstData()
        window.localStorage.setItem('bso-default-height', String(Math.floor(375 * (innerHeight / innerWidth))))

        if (!isAuthenticated || !accessAllowed) {
            const root = document.documentElement
            root.style.setProperty('--bs-dark', '#060603')
            root.style.setProperty('--bs-body-color', '#060603')
            root.style.setProperty('--bs-text-dark', '#060603')
            root.style.setProperty('--bs-heading-color', '#060603')
            root.style.setProperty('--bs-black-rgb', '6, 6, 3')
            root.style.setProperty('--bs-black-bg-rgb', '6, 6, 3')
        }
        if ((userID === null || jwt === null || !isAuthenticated) && pathname !== '/auth/register1') {
            navigate(appEntryRoute)
        }
    }, [])

    useEffect(() => {
        if (!isAuthenticated || hasAuthenticatedCurrentUser || legacyUserBootstrapRequested) return
        setLegacyUserBootstrapRequested(true)
        void dispatch(fetchUser()).then((result: any) => {
            if (result?.isSuccessful !== true) setBootstrapError(bootstrapErrorMessage(result?.textNotification))
        })
    }, [dispatch, hasAuthenticatedCurrentUser, isAuthenticated, legacyUserBootstrapRequested])

    useEffect(() => {
        if (!isAuthenticated || !accessReady || bootstrapError != null) return
        if (!accessAllowed && pathname !== LEGACY_APP_MOVED_ROUTE) {
            navigate(LEGACY_APP_MOVED_ROUTE, { replace: true })
        }
        if (accessAllowed && pathname === LEGACY_APP_MOVED_ROUTE && adminRole != null) {
            navigate(adminRoleHomeRoute(adminRole), { replace: true })
        }
    }, [accessAllowed, accessReady, isAuthenticated, navigate, pathname])

    useEffect(() => {
        if (process.env.REACT_APP_IS_DEV === 'true') {
            window.addEventListener('push-notification', (e) => {
                if ((window as any)?.notifylink && (window as any)?.notifylink.length > 0) {
                    navigate((window as any)?.notifylink)
                }
            })
            return () => {
                window.removeEventListener('push-notification', () => { })
            }
        }

    }, [])

    if (deletingAccount) {
        return (<LoadingDeleteAccount />)
    }

    const isAuthRoute = pathname.startsWith('/auth');
    const isLegacyMovedRoute = pathname === LEGACY_APP_MOVED_ROUTE

    if (isAuthenticated && !accessReady) {
        return <Loader />
    }

    if (isAuthenticated && bootstrapError != null) {
        return <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 p-8 text-center">
            <h1 className="fs-2 mb-3">Unable to open the admin panel</h1>
            <p className="text-muted mb-5">{bootstrapError}</p>
            <div className="d-flex gap-3"><button className="btn btn-primary" onClick={() => { setBootstrapError(null); setLegacyUserBootstrapRequested(false) }}>Retry</button><button className="btn btn-light-danger" onClick={() => dispatch(authSlice.actions.logout())}>Logout</button></div>
        </div>
    }

    if (isAuthenticated && accessReady && !accessAllowed) {
        if (isLegacyMovedRoute) return <LegacyAppMovedNotice />
        return <Navigate to={LEGACY_APP_MOVED_ROUTE} replace />
    }

    if (isAuthenticated && accessReady && accessAllowed && isLegacyMovedRoute && adminRole != null) {
        return <Navigate to={adminRoleHomeRoute(adminRole)} replace />
    }

    const isBsoUnitStatisticsRoute = pathname === '/landlord/my_units/statistics' || pathname.startsWith('/landlord/my_units/statistics/')
    const showSidebar = isDesktop && pathname.startsWith('/landlord') && accessAllowed && !isBsoUnitStatisticsRoute

    return (

        <AppShell
            isDesktop={isDesktop}
            showSidebar={showSidebar}
            isAuth={isAuthRoute}
            pageClassName={isBsoUnitStatisticsRoute ? 'app-shell--bso-statistics' : undefined}
        >
            <Layout>
                <Routes>
                    {<>
                        <Route path="/landlord" element={
                            <LegacyProtectedRoute accessReady={accessReady} accessAllowed={accessAllowed}>
                                <AdminRoleRouteGuard role={adminRole as AdminRole}><VoicePlayerLayout><AdminModule /></VoicePlayerLayout></AdminRoleRouteGuard>
                            </LegacyProtectedRoute>
                        }>
                            {/*  <Route path='dashboard' element={<LandlordLayout><LandlordDashboard /></LandlordLayout>} /> */}
                            <Route path="my_units" element={
                                <LandlordLayout>
                                    <MyUnitsWithFiltersSh />
                                </LandlordLayout>
                            } />
                            <Route path="my_units/statistics" element={
                                <LandlordLayout>
                                    <AdminBsoUnitStatisticsPage />
                                </LandlordLayout>
                            } />
                            <Route path="my_units/statistics/:unitId" element={
                                <LandlordLayout>
                                    <AdminBsoUnitActivityPage />
                                </LandlordLayout>
                            } />
                            {/* <Route path="my_units" element={<LandlordLayout><UIShowcase/></LandlordLayout>} /> */}
                            {/* <Route path="unit/:unitID" element={<LandlordViewUnit />} /> */}

                            <Route path='events_safe' element={<LandlordEventsPageSafe />} />
                            <Route path='landlord_service_orders' element={<LandlordServiceOrdersPage />} />
                            <Route path='sponsored_partners' element={<SponsoredPartnersAdminPage />} />
                            <Route path='insider' element={<InsiderAdminPage />} />
                            <Route path='key-viewings' element={<KeyViewingsArchivePage />} />
                            <Route path='broker-analytics' element={<BrokerAnalyticsPage />} />
                            <Route path='stat_unit/:unitID' element={<AdminUnitStatPage />} />
                            <Route path='schedule' element={<ScheduleSh />} />
                            <Route path='schedule/:unitID' element={<ScheduleSh />} />
                            {/* <Route path="services/:unitID" element={<LandlordLayout><LandlordServices /></LandlordLayout>} /> */}
                            <Route path="services/:unitID" element={<LandlordLayout><ServicesV2 /></LandlordLayout>} />
                            <Route path="settings" element={<LandlordLayout><LandlordSettings dinamicRoutes={false} /></LandlordLayout>} />
                            <Route path="settings/tutorials" element={<LandlordLayout><TutorialsList /></LandlordLayout>} />
                            <Route path="settings/terms" element={<LandlordLayout><TermsSection /></LandlordLayout>} />
                            <Route path="settings/privacy" element={<LandlordLayout><PrivacySection /></LandlordLayout>} />
                            <Route path="profile/me" element={<LandlordLayout><LandlordProfile /></LandlordLayout>} />
                            <Route path="profile/:id/:from?" element={<LandlordLayout><OtherAgentsProfile /></LandlordLayout>} />
                            <Route path="services_subscriptions" element={<LandlordLayout><MyServicesSubscriptions /></LandlordLayout>} />
                            <Route path="search" element={<LandlordLayout><Search /></LandlordLayout>} />
                            <Route path="chats" element={<LandlordLayout><ChatListSh /></LandlordLayout>} />
                            <Route path="chats/:id" element={<LandlordLayout><NewChatV2Sh /></LandlordLayout>} />
                            <Route path="referrals" element={<LandlordLayout><MyReferrals /></LandlordLayout>} />
                            <Route path="modal/v2/:modalID/:unitID?/:objectID?" element={<LandlordLayout><CustomModalV2 /></LandlordLayout>} />
                            <Route path="modal/:modalID/:unitID?/:objectID?" element={<LandlordLayout><CustomModal /></LandlordLayout>} />
                            <Route path="modalView/:modalID/:unitID?/:objectID?/:from?" element={<MyCustomModalForView />} />
                            <Route path="set_unit_modal/:modalID/:unitID/:objectID" element={<SetUnitModal />} />
                            <Route path="relist_unit/:unitID" element={<RelistUnitModal />} />
                            <Route path="menu" element={<LandlordLayout><LandlordMenuPage /></LandlordLayout>} />
                            <Route path="viewing_actions/:unitID/:viewingID" element={<LandlordLayout><ViewingActions /></LandlordLayout>} />
                            <Route path="offer_actions/:unitID/:offerID" element={<LandlordLayout><OffersActions /></LandlordLayout>} />
                            <Route path="advertise_actions/:unitID/:advertiseID" element={<LandlordLayout><AdvertiseActions /></LandlordLayout>} />
                            <Route path="tasks_actions/:taskID" element={<LandlordLayout><TasksActions /></LandlordLayout>} />
                            <Route path="portal_request_actions/:orderID" element={<LandlordLayout><PortalRequestActions /></LandlordLayout>} />
                        </Route>
                    </>
                    }

                    <Route path={LEGACY_APP_MOVED_ROUTE} element={<LegacyMovedRoute accessReady={accessReady} accessAllowed={accessAllowed} />} />

                    { /* Auth V1 */}
                    <Route path="/auth/login" element={<AuthPage />} />
                    <Route path="/auth/register_scan_passport" element={<AuthPage />} />
                    <Route path="/auth/register_scan_emirates" element={<AuthPage />} />
                    <Route path="/auth/register_scan_emirates_front" element={<AuthPage />} />
                    <Route path="/auth/write_yourself_fields" element={<AuthPage />} />
                    <Route path="/auth/register1" element={<AuthPage />} />
                    <Route path="/auth/register2" element={<AuthPage />} />
                    <Route path="/auth/phone_verify" element={<AuthPage />} />
                    <Route path="/auth/password_verify_email" element={<AuthPage />} />
                    <Route path="/auth/reset_password_final_email" element={<AuthPage />} />
                    <Route path="/auth/password_verify" element={<AuthPage />} />
                    <Route path="/auth/reset_password_final" element={<AuthPage />} />
                    <Route path="/auth/reset_password_way_phone" element={<AuthPage />} />
                    <Route path="/auth/reset_password_way_email" element={<AuthPage />} />
                    <Route path="/auth/register_upload_poa" element={<AuthPage />} />
                    <Route path="/auth/register_choose_landlord_type" element={<AuthPage />} />
                    {/* Маршруты для неавторизованных пользователей */}
                    {!isAuthenticated && (
                        <>
                            <Route path="/auth/*" element={<AuthPage />} />
                            <Route path="/auth/register1" element={<AuthPage />} />
                            <Route path="*" element={<Navigate to="/auth/login" />} />
                            <Route path="*" element={<Navigate to={appEntryRoute} />} />
                        </>
                    )}

                    {/* Редиректы для не существующих маршрутов  */}
                    {isAuthenticated && (
                        <Route
                            path="*"
                            element={
                                accessReady && accessAllowed
                                    ? (
                                        <Navigate to={LEGACY_APP_HOME_ROUTE} />
                                    )
                                    : !accessReady
                                        ? (
                                            <Loader />
                                        )
                                        : (
                                            <Navigate to={LEGACY_APP_MOVED_ROUTE} />
                                        )
                            }
                        />
                    )}
                </Routes>
            </Layout>
            {process.env.REACT_APP_IS_DEV === 'true'
                ? <div className={'badge badge-danger position-fixed'}
                    style={{
                        bottom: '10px',
                        right: '10px',
                        zIndex: 999999
                    }}
                >
                    dev
                </div>
                : <></>
            }
        </AppShell>
    )
}

